import React, { useState, useEffect } from 'react';
import FormField from './FormField';
import { stripHtmlToText } from '../../utils/richText';
import { isFormFileAnswerEmpty } from '../../utils/formFileUploads';
import { crudStyles } from '../../utils/crudStyles';
import '../../utils/crudStyles.css';

const FormRenderer = ({
  form,
  onSubmit,
  isSubmitting = false,
  initialValues = {},
  initialData = {},
  onFormDataChange,
  hideSubmitButton = false,
  hideTitle = false,
  previewMode = false,
  readOnly = false,
  onPendingFileRegister,
  onPendingFileRelease,
  documentUrlById = {}
}) => {
  const [formData, setFormData] = useState(initialValues || initialData);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(null);

  // Update form data when initialData changes
  useEffect(() => {
    console.log('FormRenderer useEffect - initialData:', initialData);
    console.log('FormRenderer useEffect - form questions:', form?.questions?.map(q => ({ label: q.label, question_id: q.question_id })));
    if (initialData !== undefined) {
      // initialData should already be in question_id format
      console.log('Setting initial data (question_id format):', initialData);
      setFormData(initialData || {});
      // Notify parent of data
      if (onFormDataChange) {
        onFormDataChange(initialData || {});
      }
    }
  }, [initialData, form]);

  // Debug current form data
  useEffect(() => {
    console.log('FormRenderer formData updated:', formData);
  }, [formData]);

  // Initialize active tab when form changes
  useEffect(() => {
    if (form && form.questions) {
      const groupedFields = form.questions.reduce((groups, field) => {
        const groupName = field.group || 'General';
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push(field);
        return groups;
      }, {});
      
      const groupNames = Object.keys(groupedFields);
      if (groupNames.length > 0 && !activeTab) {
        setActiveTab(groupNames[0]);
      }
    }
  }, [form, activeTab]);

  const handleFieldChange = (questionId, value) => {
    if (readOnly) return;
    const newFormData = {
      ...formData,
      [questionId]: value
    };
    setFormData(newFormData);
    
    // Call the parent callback if provided
    if (onFormDataChange) {
      onFormDataChange(newFormData);
    }
    
    // Clear error when user starts typing (use question_id for errors too)
    if (errors[questionId]) {
      setErrors(prev => ({
        ...prev,
        [questionId]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.questions || !Array.isArray(form.questions)) {
      return { general: 'Invalid form structure' };
    }

    form.questions.forEach(field => {
      const questionId = field.question_id;
      if (!questionId) {
        console.warn(`Question "${field.label}" is missing question_id`);
        return;
      }
      if (field.required) {
        const val = formData[questionId];
        const allowMultiple = field.allowMultiple !== false;
        const empty =
          field.type === 'file-upload'
            ? isFormFileAnswerEmpty(val, allowMultiple)
            : val == null ||
              val === '' ||
              (Array.isArray(val) && val.length === 0) ||
              (val && typeof val === 'object' && !Array.isArray(val) && !val.name && !val.documentId);
        if (empty) {
          const labelText = stripHtmlToText(field.label) || 'This field';
          newErrors[questionId] = `${labelText} is required`;
        }
      }
    });

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ general: 'Failed to submit form. Please try again.' });
    }
  };

  if (!form || !form.questions || !Array.isArray(form.questions)) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorText}>Invalid form structure</p>
      </div>
    );
  }

  // Group fields by their group property
  const groupedFields = form.questions.reduce((groups, field, index) => {
    const groupName = field.group || 'General';
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push({ ...field, originalIndex: index });
    return groups;
  }, {});

  const groupNames = Object.keys(groupedFields);
  const hasMultipleGroups = groupNames.length > 1;

  // Get fields for the active tab
  const activeFields = activeTab ? groupedFields[activeTab] || [] : [];

  return (
    <div style={styles.formContainer}>
      {previewMode ? (
        <div style={styles.previewBanner}>
          Preview — this is how respondents will see the form. Nothing is saved.
        </div>
      ) : null}
      {!hideTitle && (
        <div style={styles.formHeader}>
          <h2 style={styles.formTitle}>{form.title}</h2>
          {form.description && (
            <p style={styles.formDescription}>{form.description}</p>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      {hasMultipleGroups && (
        <div style={styles.tabContainer}>
          <div style={styles.tabList}>
            {groupNames.map((groupName) => (
              <button
                key={groupName}
                type="button"
                onClick={() => setActiveTab(groupName)}
                style={{
                  ...styles.tabButton,
                  ...(activeTab === groupName ? styles.tabButtonActive : {})
                }}
              >
                {groupName}
              </button>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form} className={readOnly ? 'form-renderer--readonly' : undefined}>
        {/* Show fields for active tab or all fields if single group */}
        {hasMultipleGroups ? (
          <div style={styles.groupContainer}>
            <div style={styles.fieldsGrid}>
              {activeFields.map((field) => (
                <div key={field.originalIndex} style={styles.fieldWrapper}>
                  <FormField
                    field={field}
                    value={formData[field.question_id]}
                    onChange={handleFieldChange}
                    error={errors[field.question_id]}
                    readOnly={readOnly}
                    onPendingFileRegister={onPendingFileRegister}
                    onPendingFileRelease={onPendingFileRelease}
                    documentUrlById={documentUrlById}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Single group - show all fields as before, but don't show group title (it's already in the expander)
          Object.entries(groupedFields).map(([groupName, fields]) => (
            <div key={groupName} style={styles.groupContainer}>
              <div style={styles.fieldsGrid}>
                {fields.map((field) => (
                  <div key={field.originalIndex} style={styles.fieldWrapper}>
                    <FormField
                      field={field}
                      value={formData[field.question_id]}
                      onChange={handleFieldChange}
                      error={errors[field.question_id]}
                      readOnly={readOnly}
                      onPendingFileRegister={onPendingFileRegister}
                      onPendingFileRelease={onPendingFileRelease}
                      documentUrlById={documentUrlById}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {errors.general && (
          <div style={styles.generalError}>
            {errors.general}
          </div>
        )}

        {!hideSubmitButton && (
          <div style={styles.submitContainer}>
            <button
              type="submit"
              disabled={isSubmitting || previewMode}
              className={crudStyles.buttonPrimaryClass}
              style={styles.submitButton}
            >
              {previewMode ? 'Submit (preview)' : isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

const styles = {
  formContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    background: 'transparent',
    borderRadius: '0',
    padding: '0',
    boxShadow: 'none'
  },
  previewBanner: {
    marginBottom: '16px',
    padding: '10px 12px',
    borderRadius: '8px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    color: '#1d4ed8',
    fontSize: '0.8rem',
    lineHeight: 1.45,
    textAlign: 'center'
  },
  formHeader: {
    marginBottom: '30px',
    textAlign: 'center'
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#111827',
    margin: '0 0 10px 0'
  },
  formDescription: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0
  },
  form: {
    display: 'flex',
    flexDirection: 'column'
  },
  tabContainer: {
    marginBottom: '12px',
    borderBottom: '1px solid #f1f5f9'
  },
  tabList: {
    display: 'flex',
    gap: '2px',
    overflowX: 'auto',
    paddingBottom: '0'
  },
  tabButton: {
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: '500',
    color: '#94a3b8',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    borderRadius: '4px 4px 0 0',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center'
  },
  tabButtonActive: {
    color: '#64748b',
    backgroundColor: '#f8fafc',
    borderBottomColor: '#cbd5e1'
  },
  groupContainer: {
    marginBottom: '0',
    padding: '0',
    background: 'transparent',
    borderRadius: '0',
    border: 'none'
  },
  groupTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#374151',
    margin: '0 0 20px 0',
    paddingBottom: '10px',
    borderBottom: '2px solid #4f46e5'
  },
  fieldsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    alignItems: 'start'
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column'
  },
  submitContainer: {
    marginTop: '12px',
    textAlign: 'right'
  },
  submitButton: {
    minWidth: '120px'
  },
  errorContainer: {
    padding: '20px',
    textAlign: 'center',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    margin: '20px 0'
  },
  errorText: {
    color: '#dc2626',
    margin: 0
  },
  generalError: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center'
  }
};

export default FormRenderer;
